import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { formatContent } from '../utils/markdownParser';
import { PostContent } from '../components/PostContent';
import { SEO } from '../components/SEO';
import { SolarDashboard } from '../components/SolarDashboard';
import { SolarHistory } from '../components/SolarHistory';
import { ProjectStepper } from '../components/ProjectStepper';
import { useSite } from '../contexts/SiteContext';

const SOLAR_MARKER = /\[solar-(?:dashboard|history)\]/i;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function DynamicPage() {
  const { slug } = useParams();
  const { settings } = useSite();
  const [page, setPage] = useState(null);
  const [allPages, setAllPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPage();
  }, [slug]);

  useEffect(() => {
    axios.get(`${API}/pages`).then((r) => setAllPages(r.data)).catch(() => {});
  }, []);

  const { prev, next } = useMemo(() => {
    if (!page) return { prev: null, next: null };
    const group = (page.menu_group || '').trim();
    const section = (page.menu_section || '').trim();
    if (!group || !section) return { prev: null, next: null };
    const includeHidden = settings?.stepper_include_hidden !== false;
    let sibs = allPages.filter(
      (p) => (p.menu_group || '').trim() === group && (p.menu_section || '').trim() === section
    );
    if (!includeHidden) sibs = sibs.filter((p) => p.show_in_menu);
    sibs.sort(
      (a, b) =>
        (a.menu_order || 0) - (b.menu_order || 0) ||
        (a.title || '').localeCompare(b.title || '')
    );
    const idx = sibs.findIndex((p) => p.slug === page.slug);
    if (idx === -1) return { prev: null, next: null };
    return {
      prev: idx > 0 ? sibs[idx - 1] : null,
      next: idx < sibs.length - 1 ? sibs[idx + 1] : null,
    };
  }, [page, allPages, settings]);

  const fetchPage = async () => {
    try {
      const response = await axios.get(`${API}/pages/${slug}`);
      setPage(response.data);
    } catch (error) {
      setError('Page not found');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    } catch (e) {
      toast.error('Could not copy link');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: page.title, url: window.location.href });
      } catch (error) {
        if (error.name !== 'AbortError') copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  if (loading) {
    return (
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/2" />
          <div className="space-y-2">
            <div className="h-4 bg-muted rounded" />
            <div className="h-4 bg-muted rounded" />
            <div className="h-4 bg-muted rounded w-5/6" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h1 className="text-2xl font-bold mb-4">Page Not Found</h1>
        <p className="text-muted-foreground mb-8">
          The page you're looking for doesn't exist or has been removed.
        </p>
        <Link to="/">
          <Button>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <article className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dynamic-page">
      <SEO
        title={page.title}
        description={page.content.substring(0, 160)}
        url={`/page/${page.slug}`}
      />
      <header className="mb-8 animate-fade-in">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter mb-4">
          {page.title}
        </h1>
        <div className="flex items-center justify-between flex-wrap gap-4">
          {page.updated_at ? (
            <p className="text-sm text-muted-foreground" data-testid="page-updated">
              Last updated {new Date(page.updated_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          ) : <span />}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleShare}
            data-testid="page-share-btn"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
        </div>
      </header>

      <ProjectStepper prev={prev} next={next} testId="stepper-top" />

      {SOLAR_MARKER.test(page.content) ? (
        <div className="animate-fade-in-delay-1 space-y-8" data-testid="page-content">
          {page.content.split(/(\[solar-(?:dashboard|history)\])/i).map((part, i) => {
            const m = part.match(/^\[solar-(dashboard|history)\]$/i);
            if (m) {
              return m[1].toLowerCase() === 'history' ? <SolarHistory key={i} /> : <SolarDashboard key={i} />;
            }
            return part.trim() ? (
              <PostContent key={i} className="prose max-w-none" html={formatContent(part)} />
            ) : null;
          })}
        </div>
      ) : (
        <PostContent
          className="prose max-w-none animate-fade-in-delay-1"
          html={formatContent(page.content)}
          testId="page-content"
        />
      )}

      <ProjectStepper prev={prev} next={next} testId="stepper-bottom" />
    </article>
  );
}
